import type {
  CategoryCoverage,
  EvidenceMatch,
  FreshnessReason,
  ItemEvidence,
  ItemOrigin,
  ItemState,
  SceneCoverage,
  SceneReviewProgress,
  StoredCoverageStatus,
  TakeawayFlag,
  TakeawayKind,
  TakeawayReadingStatus,
} from '@scoutaround/script-breakdown-shared';

import type { WorkState } from '../ui/StatusPill.js';

/**
 * THE WORDS OF THE SCENE BREAKDOWN WORKSPACE, in one place.
 *
 * Every value here is a server vocabulary (`shared/src/scene-breakdown.ts`,
 * `scene-takeaways.ts`) put into the reader's words. Nothing is derived: the
 * server decides the state, the coverage, the evidence match and the freshness,
 * and the screen only says them. Two facts are never merged into one word —
 * a scene's HUMAN review progress and its AI coverage each have their own.
 */

// ---- review progress (rule 5) -----------------------------------------------

export const PROGRESS_LABEL: Readonly<Record<SceneReviewProgress, string>> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  REVIEWED: 'Reviewed',
  NEEDS_RE_REVIEW: 'Needs re-review',
  REOPENED: 'Reopened',
};

/** The same five in the workspace's one status vocabulary (its colours). */
export const PROGRESS_STATE: Readonly<Record<SceneReviewProgress, WorkState>> = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  REVIEWED: 'COMPLETED',
  NEEDS_RE_REVIEW: 'NEEDS_ATTENTION',
  REOPENED: 'READY_FOR_REVIEW',
};

/**
 * Why a reviewed scene needs reviewing again — the server's coded reason (`InvalidationReason`,
 * `backend/src/modules/review/review-invalidation.service.ts`), in words. An unknown code (a newer
 * server) and a row from before the history (null) read the neutral sentence.
 */
const INVALIDATION_REASON: Readonly<Record<string, string>> = {
  SCENE_CONTENT_CHANGED: 'The scene’s text or heading changed.',
  SCENE_SPLIT_OR_MERGED: 'The scene was split or merged.',
  BREAKDOWN_ITEM_CHANGED: 'An item on the sheet changed.',
  REQUIREMENT_CHANGED: 'A location requirement changed.',
  SCENE_LINK_CHANGED: 'A location requirement was linked or unlinked.',
  NEW_AI_BASELINE: 'A new AI breakdown was read.',
  ITEM_DECIDED: 'An item was decided after the review.',
};

export function progressReasonText(reason: string | null): string {
  return (reason === null ? undefined : INVALIDATION_REASON[reason]) ?? 'Something changed after it was reviewed.';
}

// ---- AI coverage (rule 2) -----------------------------------------------------

/** A scene's coverage in one word: shown beside its review progress, never merged with it. */
export const SCENE_COVERAGE_LABEL: Readonly<Record<SceneCoverage, string>> = {
  FULL: 'Full',
  SELECTIVE: 'Selected categories',
  PARTIAL: 'Partly read',
  NOT_READ: 'Not read',
  OUT_OF_DATE: 'Out of date',
  NOT_RECORDED: 'Coverage not recorded',
  NOT_ANALYSED: 'Not analysed yet',
};

/** The longer sentence a scene's coverage marker carries as its accessible name. */
export const SCENE_COVERAGE_SENTENCE: Readonly<Record<SceneCoverage, string>> = {
  FULL: 'AI reading: every category was checked',
  SELECTIVE: 'AI reading: the categories it selected were checked; the rest were not',
  PARTIAL: 'AI reading: partly read — some categories were not fully answered',
  NOT_READ: 'AI reading: not read',
  OUT_OF_DATE: 'AI reading: read before the scene changed',
  NOT_RECORDED: 'AI reading: coverage not recorded for this reading',
  NOT_ANALYSED: 'AI reading: not analysed yet',
};

/**
 * What an EMPTY category says. "None found" is a claim about the screenplay, so it is made
 * only when every part of the scene the category needed was examined (`CHECKED`); every other
 * coverage says what was or was not examined instead.
 */
export const EMPTY_CATEGORY_TEXT: Readonly<Record<CategoryCoverage, string>> = {
  CHECKED: 'None found',
  INCOMPLETE: 'Partly read',
  SKIPPED: 'Not checked: the AI reading did not select this category',
  FAILED: 'Not read: the request failed',
  UNREAD: 'Not read',
  OUT_OF_DATE: 'Read before the scene changed',
  NOT_RECORDED: 'Coverage not recorded',
  NOT_ANALYSED: 'Not analysed yet',
};

/** The note beside a category that HAS items but was not fully examined. */
export const PARTIAL_CATEGORY_NOTE: Readonly<Partial<Record<CategoryCoverage, string>>> = {
  INCOMPLETE: 'Partly read: the AI reading did not answer for every part of the scene',
  SKIPPED: 'Not checked by the AI reading; these were added or cued',
  FAILED: 'The AI request for this category failed',
  UNREAD: 'Not read by the AI',
  OUT_OF_DATE: 'Read before the scene changed',
  NOT_RECORDED: 'Coverage not recorded',
};

/** A later attempt that did worse than what is shown (S2): the earlier reading stays. */
export function lastAttemptText(status: StoredCoverageStatus): string {
  if (status === 'FAILED') return 'A later reading failed; showing the earlier one';
  if (status === 'SKIPPED') return 'A later reading skipped this category; showing the earlier one';
  if (status === 'UNREAD') return 'A later reading did not reach this category; showing the earlier one';

  return 'A later reading did less; showing the earlier one';
}

// ---- items (rule 4, rule 6) ---------------------------------------------------------

export const ORIGIN_LABEL: Readonly<Record<ItemOrigin, string>> = {
  AI_READING: 'Read by AI',
  CHARACTER_CUE: 'From a character cue',
  ADDED_BY_HAND: 'Added by you',
};

export const ITEM_STATE_LABEL: Readonly<Record<ItemState, string>> = {
  AWAITING: 'Awaiting your decision',
  ON_SHEET: 'On the sheet',
  NOT_NEEDED: 'Not needed',
};

export const ITEM_STATE_WORK: Readonly<Record<ItemState, WorkState>> = {
  AWAITING: 'READY_FOR_REVIEW',
  ON_SHEET: 'CONFIRMED',
  NOT_NEEDED: 'EXCLUDED',
};

// ---- evidence (rule 3) ------------------------------------------------------------

/**
 * What was verified about an item's place in the text. Only VERIFIED and HEADING are verified
 * evidence; an ambiguous quote names how many times it appears and never picks one.
 */
export function evidenceLabel(evidence: Pick<ItemEvidence, 'match' | 'ranges'>): string {
  const labels: Readonly<Record<EvidenceMatch, string>> = {
    VERIFIED: 'Verified quote',
    HEADING: 'Quote in the heading',
    RELOCATED: 'Found elsewhere in the scene',
    AMBIGUOUS: `Appears ${String(evidence.ranges.length)} times — check which`,
    APPROXIMATE: 'Approximate match',
    NONE: 'Not located in the text',
  };

  return labels[evidence.match];
}

// ---- key takeaways (rule 6, rule 7) ----------------------------------------------

export const TAKEAWAY_KIND_LABEL: Readonly<Record<TakeawayKind, string>> = {
  COMPLEXITY: 'Complexity',
  OPEN_DECISION: 'Open decision',
  DEPENDENCY: 'Dependency',
  CONTINUITY: 'Continuity',
  SPECIALIST_REVIEW: 'Specialist review',
  LOGISTICS: 'Logistics',
};

export const FRESHNESS_REASON_TEXT: Readonly<Record<FreshnessReason, string>> = {
  TEXT_CHANGED: 'The scene’s text changed',
  ITEMS_CHANGED: 'Its items changed',
  COVERAGE_CHANGED: 'What the reading examined changed',
  PLAN_CHANGED: 'The production plan changed',
};

export const TAKEAWAY_FLAG_TEXT: Readonly<Record<TakeawayFlag, string>> = {
  ITEM_NOT_NEEDED: 'Rests on an item marked not needed',
  DECISION_MADE: 'The decision it names has since been made',
};

export const TAKEAWAY_STATUS_TEXT: Readonly<Record<Exclude<TakeawayReadingStatus, 'READ'>, string>> = {
  READ_EMPTY: 'Nothing stood out',
  UNREAD: 'Not read',
  FAILED: 'Not read',
  UNAVAILABLE: 'Not available for this production’s AI connection',
};

// ---- figures as a reader says them ------------------------------------------------

/** A scene's length as a script supervisor says it: "2 3/8 pp", "4/8 pp", "1 pp". */
export function pageLength(pageEighths: number | null): string | null {
  if (pageEighths === null || pageEighths <= 0) return null;
  const whole = Math.floor(pageEighths / 8);
  const eighths = pageEighths % 8;
  const fraction = eighths === 0 ? '' : `${String(eighths)}/8`;

  return `${whole === 0 ? '' : String(whole)}${whole !== 0 && fraction !== '' ? ' ' : ''}${fraction} pp`;
}

/** "Scene 12", or its place when the screenplay numbers none. */
export function sceneName(scene: { readonly sceneNumber: string | null; readonly ordinal: number }): string {
  return `Scene ${scene.sceneNumber ?? String(scene.ordinal)}`;
}

/** "1 item", "3 items". */
export function itemsText(n: number): string {
  return `${String(n)} ${n === 1 ? 'item' : 'items'}`;
}
